import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { TooltipContent } from "./TooltipContent";
import ReactDOMServer from "react-dom/server";
import {
    DEFAULT_AXIS_LABEL_PROPS,
    DEFAULT_AXIS_PROPS,
    DEFAULT_TOOLTIP_PROPS,
} from "~/modules/visualization/consts";
import { AggregateItem } from "~/modules/token_timeseries/types";
import { roundN } from "~/utils/functions/roundN";
import { RWA_COIN_ICON } from "~/consts";

// // // //

// NOTE - this is a copy of formatCurrencyValueCompact, except with 0 fractional digits and integrated signage + $ symbol
function formatCurrencyValueCompactAxis(value: number): string {
    // Positive or negative?
    const absValue = Math.abs(value);
    const isNegative = absValue !== value;

    // Handle positive/negative signage
    const signage = isNegative ? "-" : "";

    // Handle billion or greater
    if (absValue >= 1_000_000_000) {
        return `${signage}$${roundN(absValue / 1_000_000_000, 1).toLocaleString("en-US", { maximumFractionDigits: 1, minimumFractionDigits: 1 })}B`;
    }
    // Handle million or greater but less than a billion
    else if (absValue >= 1_000_000) {
        return `${signage}$${roundN(absValue / 1_000_000, 1).toLocaleString("en-US", { maximumFractionDigits: 0, minimumFractionDigits: 0 })}M`;
    }
    // Handle thousand or greater but less than a million
    else if (absValue >= 1_000) {
        return `${signage}$${roundN(absValue / 1_000, 1).toLocaleString("en-US", { maximumFractionDigits: 0, minimumFractionDigits: 0 })}K`;
    }

    // Handle hundred or greater but less than a thousand
    else if (absValue >= 10) {
        return `${signage}$${roundN(absValue, 1).toLocaleString("en-US", { maximumFractionDigits: 0, minimumFractionDigits: 0 })}`;
    }

    return `${signage}$${absValue.toLocaleString("en-US", {
        maximumFractionDigits: 1,
        minimumFractionDigits: 1,
    })}`;
}

// // // //

export type NetFlowsScale = "linear" | "symlog";

// Width (px) reserved for the category-axis labels (the asset-name "legend" on
// the left of the chart). The label text is truncated with a CSS ellipsis when
// it exceeds this width.
const DEFAULT_LEGEND_WIDTH = 100;

// Match the app's font (Tailwind `font-sans` → Lausanne) so SVG-rendered text
// (data labels, axis ticks) doesn't fall back to the Highcharts default.
const CHART_FONT_FAMILY =
    'Lausanne, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

export interface NetFlowsItemLabel {
    primary: string;
    secondary: string;
}

export type NetFlowsItemLabels = Record<number, NetFlowsItemLabel>;

// Symmetric-log (symlog) transform: log-compresses magnitude while preserving
// sign, so a diverging bar chart with positive *and* negative values (and zero)
// can span many orders of magnitude. A plain logarithmic axis can't — log is
// undefined for values <= 0.
const SYMLOG_LINEAR_THRESHOLD = 1;

function symlog(value: number): number {
    return (
        Math.sign(value) *
        Math.log10(1 + Math.abs(value) / SYMLOG_LINEAR_THRESHOLD)
    );
}

function symlogInverse(transformed: number): number {
    return (
        Math.sign(transformed) *
        SYMLOG_LINEAR_THRESHOLD *
        (Math.pow(10, Math.abs(transformed)) - 1)
    );
}

// Tick positions (in transformed space) at 0 and ±powers of ten within range.
function symlogTickPositions(rawMin: number, rawMax: number): number[] {
    const maxAbs = Math.max(Math.abs(rawMin), Math.abs(rawMax), 1);
    const maxExp = Math.ceil(Math.log10(maxAbs));
    const rawTicks = new Set<number>([0]);
    for (let exp = 3; exp <= maxExp; exp++) {
        const magnitude = Math.pow(10, exp);
        if (magnitude <= rawMax) {
            rawTicks.add(magnitude);
        }
        if (-magnitude >= rawMin) {
            rawTicks.add(-magnitude);
        }
    }
    return Array.from(rawTicks)
        .map(symlog)
        .sort((a, b) => a - b);
}

function getChartOptions(props: {
    items: AggregateItem[];
    height: number;
    scale: NetFlowsScale;
    animation: boolean;
    label: string;
    legendWidth: number;
    showIcon: boolean;
    itemLabels?: NetFlowsItemLabels;
    onClick?: (item: AggregateItem) => void;
}) {
    const {
        items,
        height,
        scale,
        animation,
        label,
        legendWidth,
        showIcon,
        itemLabels,
        onClick,
    } = props;
    const isSymlog = scale === "symlog";

    const emptySeriesData = Array(items.length).fill(null);

    const series = items
        .map((item) => {
            return {
                name: item.group.name,
                data: [...emptySeriesData],
                net_flows_value: item.value,
                color: item.group.color,
                icon_url: item.group.icon_url,
                itemLabel: itemLabels?.[item.group.id],
                // Original item, surfaced back through the onClick handler.
                aggregateItem: item,
            };
        })
        .sort((a, b) => b.net_flows_value - a.net_flows_value)
        .map((series, index) => {
            const data = [...emptySeriesData];

            // Plot the transformed value under symlog; the original is kept on
            // `net_flows_value` for the tooltip + data labels.
            data[index] = isSymlog
                ? symlog(series.net_flows_value)
                : series.net_flows_value;

            return {
                ...series,
                data,
            };
        });

    const rawMin = Math.min(...series.map((s) => s.net_flows_value));
    const rawMax = Math.max(...series.map((s) => s.net_flows_value));

    // Build categories
    // NOTE - this MUST match the order in the series array
    const categories = series.map((item) => item.name);

    const opts = {
        chart: {
            type: "bar",
            responsive: true,
            height,
            backgroundColor: "transparent",
            animation,
            marginLeft: legendWidth + 25,
            spacingRight: 30,
            style: {
                fontFamily: CHART_FONT_FAMILY,
            },
        },
        title: {
            text: null, // Hiding the title
        },
        xAxis: {
            lineColor: "var(--border)", // Color the axis line
            categories,
            labels: {
                useHTML: true,
                padding: 0, // Add padding around labels
                distance: 15, // Increase distance between labels and axis
                align: "left", // Left-justify the asset-name legend
                reserveSpace: true,
                formatter: function (
                    this: Highcharts.AxisLabelsFormatterContextObject,
                ): string {
                    const index = categories.indexOf(this.value.toString());
                    if (index !== -1) {
                        const item = series[index];

                        const iconHTML = showIcon
                            ? `<img src="${item.icon_url ?? RWA_COIN_ICON}" alt="${item.name}" style="width: 16px; height: 16px; flex-shrink: 0;" class="rounded-full">`
                            : "";
                        const labelHTML = item.itemLabel
                            ? `<span style="display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 12px;">${item.itemLabel.primary}</span>
                               <span style="display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 11px; color: var(--muted-foreground);">${item.itemLabel.secondary}</span>`
                            : `<span style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${item.name}</span>`;

                        return `<div style="padding: 0; font-size: 12px; width: ${legendWidth}px; display: flex; align-items: center; gap: 4px; color: var(--foreground);">
                            ${iconHTML}
                            <span style="display: block; min-width: 0; overflow: hidden; width: 100%;">${labelHTML}</span>
                            </div>`;
                    }
                    return this.value.toString();
                },
            },
        },
        yAxis: {
            ...DEFAULT_AXIS_PROPS,
            lineColor: "var(--border)",
            tickColor: "var(--border)",
            gridLineColor: "var(--border)",
            min: isSymlog ? symlog(rawMin) : rawMin,
            max: isSymlog ? symlog(rawMax) : rawMax,
            // Explicit on both branches so a chart.update() can't leave stale
            // (transformed) tick positions behind when switching back to linear.
            tickPositions: isSymlog
                ? symlogTickPositions(rawMin, rawMax)
                : undefined,
            title: {
                text: null,
            },
            labels: {
                ...DEFAULT_AXIS_LABEL_PROPS,
                enabled: true,
                overflow: "allow",
                formatter: function (
                    this: Highcharts.AxisLabelsFormatterContextObject,
                ): string {
                    // Invert the transform so labels still read real dollars.
                    const value = isSymlog
                        ? symlogInverse(Number(this.value))
                        : Number(this.value);
                    return `<div style="padding: 0; font-size: 12px; max-width: 100px; color: var(--foreground);">${formatCurrencyValueCompactAxis(value)}</div>`;
                },
            },
        },
        tooltip: {
            useHTML: true,
            shared: true, // Changed to true to show tooltips when hovering on the same Y position
            // // // //
            // Renders the tooltip outside the chart's SVG
            // https://api.highcharts.com/highcharts/tooltip.outside
            outside: true,
            backgroundColor: "var(--card)",
            style: {
                zIndex: 225,
            },
            positioner: function (
                labelWidth: number,
                labelHeight: number,
                point: { plotX: number; plotY: number },
            ) {
                return {
                    x: point.plotX - labelWidth / 2,
                    y: point.plotY - labelHeight - 10, // Position above the point with 10px gap
                };
            },
            // // // //
            ...DEFAULT_TOOLTIP_PROPS,
            formatter: function (this: any) {
                const points = this.points || [this.point];
                const point = points[0];

                // Get the data for TooltipContent
                const name = point.series.options.name;
                const net_flows_value = point.series.options.net_flows_value;
                const icon_url = point.series.options.icon_url;

                return ReactDOMServer.renderToString(
                    <TooltipContent
                        name={name}
                        value={net_flows_value}
                        label={label}
                        showIcon={showIcon}
                        iconUrl={icon_url ?? RWA_COIN_ICON}
                        showClickHint={onClick !== undefined}
                    />,
                );
            },
        },
        accessibility: {
            enabled: false,
        },
        credits: {
            enabled: false,
        },
        plotOptions: {
            series: {
                stacking: "normal",
                stickyTracking: true, // Enable sticky tracking for tooltips
                enableMouseTracking: true, // Ensure mouse tracking is enabled
                cursor: onClick ? "pointer" : undefined,
                point: {
                    events: {
                        click: function (this: any) {
                            onClick?.(this.series.options.aggregateItem);
                        },
                    },
                },
                states: {
                    inactive: {
                        opacity: 1, // Keep inactive bars at full opacity
                    },
                    hover: {
                        brightness: 0.1, // Reduce brightness change on hover
                    },
                },
            },
            bar: {
                borderWidth: 0,
                pointPadding: 0.1,
                groupPadding: 0.1,
                dataLabels: {
                    enabled: true,
                    align: "right",
                    inside: true,
                    formatter: function (this: any): string {
                        // Read the original (untransformed) value off the series
                        // so the label is correct under symlog too.
                        return formatCurrencyValueCompactAxis(
                            Number(this.series.options.net_flows_value),
                        );
                    },
                    style: {
                        fontWeight: "500",
                        textOutline: "none",
                        color: "var(--foreground)",
                    },
                },
            },
        },
        legend: {
            enabled: false, // Hiding the legend
        },
        series,
    };

    return opts;
}

// // // //

/**
 * Net Flows Chart component for Asset Aggregates
 */
export function NetFlowsChart(props: {
    items: AggregateItem[];
    height?: number;
    scale?: NetFlowsScale;
    /** Enable chart animation. Defaults to `true` (current behavior). */
    animation?: boolean;
    /** Label shown in the tooltip's value row. Defaults to `Net Flows`. */
    label?: string;
    /**
     * Width (px) reserved for the asset-name labels on the left of the chart.
     * Defaults to `100` (current behavior).
     */
    legendWidth?: number;
    /**
     * Show each asset's icon in the legend. Defaults to `false`.
     */
    showIcon?: boolean;
    /** Optional two-line labels keyed by aggregate group id. */
    itemLabels?: NetFlowsItemLabels;
    /** Called with the underlying item when a bar is clicked. */
    onClick?: (item: AggregateItem) => void;
}) {
    const {
        items,
        height = 390,
        scale = "linear",
        animation = true,
        label = "Net Flows",
        legendWidth = DEFAULT_LEGEND_WIDTH,
        showIcon = false,
        itemLabels,
        onClick,
    } = props;

    // DEBUG - display empty message
    if (items.length === 0) {
        return <p className="text-red-600 font-semibold text-xl">NO DATA</p>;
    }

    const options = getChartOptions({
        items,
        height,
        scale,
        animation,
        label,
        legendWidth,
        showIcon,
        itemLabels,
        onClick,
    });

    // Remount on scale change so Highcharts builds a fresh axis instead of
    // merging the new options over the previous scale's (which leaves stale
    // tick positions / transformed values behind).
    return (
        <HighchartsReact
            key={scale}
            highcharts={Highcharts}
            options={options}
        />
    );
}
